import firebase from '@/api/config/FirebaseConfig'
import ForeplayAPI from '@/api/foreplayServer'
import TAG_COLORS from '../TagColors'

// Icons
import * as Icons from '../../components/globals/Icons/FilterIcons'
import DCOIcon from '../../components/globals/Icons/DCOIcon.vue'
import TargetMarketIcon from '../../components/globals/Icons/TargetMarketIcon.vue'
import B2CIcon from '../../components/globals/Icons/B2CIcon.vue'
import B2BIcon from '../../components/globals/Icons/B2BIcon.vue'

// Custom Filter Components
import VideoLengthFilter from '../../components/search/TopBarComponents/CustomFilters/VideoLengthFilter.vue'
import VideoLengthSearchReplacer from '../../components/filters/dropdown/VideoLengthSearchReplacer.vue'
import RunTimeFilter from '../../components/search/TopBarComponents/CustomFilters/RunTimeFilter.vue'

// Category Header/Footer Components
import MetaOnlyDisclaimer from '../../components/filters/dropdown/MetaOnlyDisclaimer.vue'
import ImageAdsOnlyDisclaimer from '../../components/filters/dropdown/ImageAdsOnlyDisclaimer.vue'

// Option Extra Label Components
import MetaOnlyLabel from '../../components/filters/dropdown/MetaOnlyLabel.vue'

// =-=-= ASYNC FETCHERS (one-shot remote lists) =-=-=

export async function fetchStaticFilterData () {
  const user = firebase.auth().currentUser
  const db = firebase.firestore()
  if (!user || !db) return { languages: [], niches: [] }

  const [languages, niches] = await Promise.all([
    fetchLanguages(db),
    fetchNiches(db)
  ])

  return { languages, niches }
}

async function fetchLanguages (db) {
  const snapshot = await db.collection('languages').get()
  const parsedDocs = snapshot.docs?.map(doc => {
    const docData = { ...(doc.data() || []) }
    return { name: docData.language }
  }) || []
  return parsedDocs.sort((a, b) => (a.name > b.name ? 1 : -1))
}

async function fetchNiches (db) {
  const snapshot = await db.collection('niches').get()
  const parsedDocs = snapshot.docs?.map(doc => {
    const docData = { ...(doc.data() || []) }
    return {
      name: docData.niche,
      icon: nicheIconMap.get(docData.niche) || Icons.OtherNicheIcon
    }
  }) || []
  const sortedDocs = parsedDocs.sort((a, b) => (a.name > b.name ? 1 : -1))
  // Pin "Other" to the end of the list
  const otherIndex = sortedDocs.findIndex((d) => d.name === 'Other')
  if (otherIndex !== -1) {
    const [otherNiche] = sortedDocs.splice(otherIndex, 1)
    sortedDocs.push(otherNiche)
  }
  return sortedDocs
}

// =-=-= REMOTE OPTION SOURCES (paged and searched by the API) =-=-=
// For categories whose options are too many to hold in `options`. Such a category sets `remoteOptions`:
//   pageSize: options per page
//   fetchPage({ page, limit, search }) -> { options, hasMore }: one page, already searched and ordered by the API
//   fetchByValues(values) -> options: the options for the given filter values, for labelling selections
//     that haven't been loaded (missing values are left out)
// Options take the same shape as `options` entries, and the category keeps no `options` of its own.

const toBrandOption = (brand) => ({
  id: brand.id,
  name: brand.name,
  image: brand.avatar_url
})

// Spyder brands the user (or their team) has saved ads for: A-Z, or by match quality when searching
async function fetchLibraryBrandOptions ({ page, limit, search }) {
  const { data, pages } = await ForeplayAPI.Brands.getLibraryBrands({ page, limit, search })
  return {
    options: (data || []).map(toBrandOption),
    hasMore: page < (pages || 0)
  }
}

async function fetchBrandOptionsByIds (brandIds) {
  const brands = await ForeplayAPI.Brands.getByBrandIds(brandIds)
  return brands.map(toBrandOption)
}

// =-=-= PURE BUILDERS =-=-=

function buildBoardOptions (boards = []) {
  return boards.map(board => ({
    ...board,
    name: board.name.slice(1),
    icon: Icons.BoardIcon
  }))
}

function buildTagOptions (tags = []) {
  return tags.map(tag => ({
    ...tag,
    icon: Icons.TagIcon,
    iconStroke: TAG_COLORS[tag.color].stroke,
    iconFill: TAG_COLORS[tag.color].fill
  }))
}

function buildSavedFilterOptions (savedFilters = [], currentProduct = null) {
  if (!currentProduct) return savedFilters
  return savedFilters.filter(filter => filter.product === currentProduct)
}

// Assembles the full filter categories array from the provided slices. If
// `currentProduct` is supplied, the result is also scoped to that product.
export function buildFilterCategories ({
  languages = [],
  niches = [],
  savedFilters = [],
  boards = [],
  tags = [],
  currentProduct = null
} = {}) {
  const savedFilterOptions = buildSavedFilterOptions(savedFilters, currentProduct)
  const boardOptions = buildBoardOptions(boards)
  const tagOptions = buildTagOptions(tags)

  const filterCategories = [
    {
      id: 'saved_filters',
      products: ['library', 'discovery', 'spyder', 'lens'],
      searchPlaceholder: 'Search Saved Filters...',
      options: savedFilterOptions
    },
    {
      id: 'boards',
      name: 'Boards',
      products: ['library'],
      searchPlaceholder: 'Search Boards...',
      icon: Icons.BoardIcon,
      serverKey: { or: 'orBoardId', and: 'andBoardId', exclude: 'excludeBoardId' },
      optionValueKey: 'id',
      valueTransform: (value) => value,
      options: boardOptions
    },
    {
      id: 'brands',
      name: 'Brands',
      products: ['library'],
      searchPlaceholder: 'Search Brands...',
      icon: Icons.BrandIcon,
      serverKey: { or: 'orBrands', and: 'andBrands', exclude: 'excludeBrands' },
      optionValueKey: 'id',
      valueTransform: (value) => value,
      remoteOptions: {
        pageSize: 100,
        fetchPage: fetchLibraryBrandOptions,
        fetchByValues: fetchBrandOptionsByIds
      }
    },
    {
      id: 'tags',
      name: 'Tags',
      products: ['library'],
      searchPlaceholder: 'Search Tags...',
      icon: Icons.TagIcon,
      serverKey: { or: 'orTag', and: 'andTag', exclude: 'excludeTag' },
      optionValueKey: 'id',
      valueTransform: (value) => value,
      options: tagOptions
    },
    {
      id: 'format',
      name: 'Format',
      products: ['library', 'discovery', 'spyder', 'lens'],
      searchPlaceholder: 'Search Formats...',
      icon: Icons.FormatIcon,
      serverKey: { or: 'orFormat', and: 'andFormat', exclude: 'excludeFormat' },
      optionValueKey: 'name',
      valueTransform: (value) => value.toLowerCase(),
      options: [
        { name: 'Video', icon: Icons.VideoFormatIcon },
        { name: 'Image', icon: Icons.ImageFormatIcon },
        { name: 'Carousel', icon: Icons.CarouselFormatIcon },
        { name: 'DCO', icon: DCOIcon, optionExtraLabel: MetaOnlyLabel }
      ]
    },
    {
      id: 'status',
      name: 'Status',
      products: ['library', 'discovery', 'spyder', 'lens'],
      searchPlaceholder: 'Search Statuses...',
      icon: Icons.StatusIcon,
      categoryFooter: MetaOnlyDisclaimer,
      serverKey: { or: 'orLiveStatus', and: 'andLiveStatus' },
      optionValueKey: 'value',
      valueTransform: (value) => value,
      isSingleSelect: true,
      disableExclude: true,
      options: [
        { name: 'Still Running', value: 'live', icon: Icons.StatusIndicatorIcon, iconStroke: '', iconFill: '#00A879' },
        { name: 'Not Running', value: 'dead', icon: Icons.StatusIndicatorIcon, iconStroke: '', iconFill: '#9DA3AE' }
      ]
    },
    {
      id: 'niche',
      name: 'Niche',
      products: ['library', 'discovery', 'spyder', 'lens'],
      searchPlaceholder: 'Search Niches...',
      icon: Icons.NicheIcon,
      serverKey: { or: 'orNiche', and: 'andNiche', exclude: 'excludeNiche' },
      optionValueKey: 'name',
      valueTransform: (value) => value,
      options: niches
    },
    {
      id: 'platform',
      name: 'Platform',
      products: ['library', 'discovery', 'spyder', 'lens'],
      searchPlaceholder: 'Search Platforms...',
      icon: Icons.PlatformIcon,
      serverKey: { or: 'orPublisherPlatform', and: 'andPublisherPlatform', exclude: 'excludePublisherPlatform' },
      optionValueKey: 'name',
      valueTransform: (value) => value.toLowerCase(),
      options: [
        { name: 'Facebook', icon: Icons.FacebookPlatformIcon, iconStroke: '', iconClass: 'w-5 h-5 p-0.5' },
        { name: 'Instagram', icon: Icons.InstagramPlatformIcon, iconStroke: '', iconClass: 'w-5 h-5 p-0.5' },
        { name: 'TikTok Organic', icon: Icons.TikTokPlatformIcon, iconStroke: '', iconClass: 'w-5 h-5 p-0.5' },
        { name: 'TikTok Ads', icon: Icons.TikTokPlatformIcon, iconStroke: '', iconClass: 'w-5 h-5 p-0.5' },
        { name: 'YouTube', icon: Icons.YouTubePlatformIcon, iconStroke: '', iconClass: 'w-5 h-5 p-0.5' },
        { name: 'LinkedIn', icon: Icons.LinkedInPlatformIcon, iconStroke: '', iconClass: 'w-5 h-5 p-0.5' }
      ]
    },
    {
      id: 'language',
      name: 'Language',
      products: ['library', 'discovery', 'spyder'],
      searchPlaceholder: 'Search Languages...',
      icon: Icons.LanguageIcon,
      serverKey: { or: 'orLanguages', and: 'andLanguages', exclude: 'excludeLanguages' },
      optionValueKey: 'name',
      valueTransform: (value) => value,
      options: languages
    },
    {
      id: 'content_style',
      name: 'Content Style',
      products: ['discovery', 'lens'],
      searchPlaceholder: 'Search Styles...',
      icon: Icons.ContentStyleIcon,
      categoryHeader: ImageAdsOnlyDisclaimer,
      serverKey: { or: 'orContentFilter', and: 'andContentFilter', exclude: 'excludeContentFilter' },
      optionValueKey: 'name',
      valueTransform: (value) => value,
      options: [
        { name: 'Before and After' },
        { name: 'Facts and Stats' },
        { name: 'Features and Benefits' },
        { name: 'Holiday - Seasonal' },
        { name: 'Media and Press' },
        { name: 'Promotion and Discount' },
        { name: 'Reasons why' },
        { name: 'Testimonial - Review' },
        { name: 'Us vs Them' }
      ].map(contentStyle => ({ name: contentStyle.name, icon: Icons.OtherNicheIcon }))
    },
    {
      id: 'target_market',
      name: 'Target Market',
      products: ['discovery', 'lens'],
      searchPlaceholder: 'Search Targets...',
      icon: TargetMarketIcon,
      serverKey: { or: 'orTargetMarket', and: 'andTargetMarket', exclude: 'excludeTargetMarket' },
      optionValueKey: 'value',
      valueTransform: (value) => value,
      options: [
        { name: 'B2C', value: 'b2c', icon: B2CIcon },
        { name: 'B2B', value: 'b2b', icon: B2BIcon }
      ]
    },
    {
      id: 'video_length',
      name: 'Video Length',
      products: ['library', 'discovery', 'spyder', 'lens'],
      icon: Icons.VideoLengthIcon,
      component: VideoLengthFilter,
      componentWidth: '220px',
      searchReplacer: VideoLengthSearchReplacer,
      defaultSelection: { min: null, max: null },
      getValueText: getVideoLengthText,
      getPresetPreviewText: getVideoLengthText,
      getServerKeys: (selectedVideoLength) => {
        // Must be destructured in server query builder
        const { min, max } = selectedVideoLength
        return {
          videoLengthMin: min ?? null,
          videoLengthMax: max ?? null
        }
      }
    },
    {
      id: 'run_time',
      name: 'Run Time',
      products: ['library', 'discovery'],
      searchPlaceholder: 'Search Run Times...',
      icon: Icons.RunTimeIcon,
      component: RunTimeFilter,
      componentWidth: '180px',
      defaultSelection: { min: null, max: null },
      getValueText: getRunTimeText,
      getPresetPreviewText: getRunTimeText,
      getServerKeys: (selectedRunTime) => {
        const { min, max } = selectedRunTime
        return {
          runTimeMin: min ?? null,
          runTimeMax: max ?? null
        }
      }
    }
  ]

  if (!currentProduct) return filterCategories
  return filterCategories.filter(category => category.products.includes(currentProduct))
}

// ========= HELPERS ==========

// Video Length Helpers

const getVideoLengthText = (selection) => {
  const { min, max } = selection
  const start = min ? formatTimeFromSeconds(min) : '0:00'
  const end = max !== null ? formatTimeFromSeconds(max) : ''
  return max !== null ? `${start}-${end}` : `${start}+`
}

const formatTimeFromSeconds = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes.toString().padStart(1, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

// Run Time Helpers
const getRunTimeText = (selection) => {
  const { min, max } = selection
  if (min === null && max !== null) return `Under ${max} days`
  if (min !== null && max === null) return `Over ${min - 1} days`
  if (min !== null && max !== null) return `${min}-${max} days`
  return 'Any'
}

const nicheIconMap = new Map([
  ['Accessories', Icons.AccessoriesNicheIcon],
  ['Alcohol', Icons.AlcoholNicheIcon],
  ['App/Software', Icons.SoftwareNicheIcon],
  ['Automotive', Icons.AutomotiveNicheIcon],
  ['Beauty', Icons.BeautyNicheIcon],
  ['Book/Publishing', Icons.BookNicheIcon],
  ['Business/Professional', Icons.BusinessNicheIcon],
  ['Charity/NFP', Icons.CharityNicheIcon],
  ['Education', Icons.EducationNicheIcon],
  ['Entertainment', Icons.EntertainmentNicheIcon],
  ['Fashion', Icons.FashionNicheIcon],
  ['Finance', Icons.FinanceNicheIcon],
  ['Food/Drink', Icons.FoodNicheIcon],
  ['Games', Icons.GamesNicheIcon],
  ['Government', Icons.GovernmentNicheIcon],
  ['Health/Wellness', Icons.HealthNicheIcon],
  ['Home/Garden', Icons.HomeNicheIcon],
  ['Insurance', Icons.InsuranceNicheIcon],
  ['Jewelry/Watches', Icons.JewelryNicheIcon],
  ['Kids/Baby', Icons.KidsNicheIcon],
  ['Media/News', Icons.MediaNicheIcon],
  ['Medical', Icons.MedicalNicheIcon],
  ['Pets', Icons.PetsNicheIcon],
  ['Real Estate', Icons.RealEstateNicheIcon],
  ['Service Business', Icons.ServiceNicheIcon],
  ['Sports/Outdoors', Icons.SportsNicheIcon],
  ['Tech', Icons.TechNicheIcon],
  ['Travel', Icons.TravelNicheIcon],
  ['Other', Icons.OtherNicheIcon]
])
